import {
  BaseQueryFn,
  buildCreateApi,
  coreModule,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  reactHooksModule,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { REHYDRATE } from 'redux-persist';

import Alternativa, {
  FinderAlternativas,
  RetornoAlternativa,
} from 'Interface/Alternativas';
import Assinatura, {
  FinderAssinaturas,
  FinderHowManySubscriptionsInterface,
  RetornoAssinatura,
} from 'Interface/Assinaturas';
import AssistirMaisTarde, {
  FinderAssistirMaisTarde,
} from 'Interface/AssistirMaisTarde';
import Aula, { FinderAulas, RetornoAula } from 'Interface/Aulas';
import AulaAssistida, {
  FinderAulasAssistidas,
  RetornoAulaAssistida,
} from 'Interface/AulasAssistidas';
import AulaConcluida, {
  FinderAulasConcluidas,
  RetornoAulaConcluida,
} from 'Interface/AulasConcluidas';
import Avaliacao, {
  FinderAvaliacoes,
  RetornoAvaliacao,
} from 'Interface/Avaliacoes';
import AvaliacaoRespondida, {
  FinderAvaliacoesRespondidas,
  RetornoAvaliacaoRespondida,
} from 'Interface/AvaliacoesRespondidas';
import Categoria, {
  FinderCategorias,
  RetornoCategoria,
} from 'Interface/Categorias';
import Certificado, {
  FinderCertificados,
  RetornoCertificado,
  RetornoFretes,
} from 'Interface/Certificados';
import Curso, {
  FinderCursos,
  FinderTotalCadastradoCursos,
  RetornoCurso,
} from 'Interface/Cursos';
import Matricula, {
  FinderMatriculas,
  RetornoMatricula,
} from 'Interface/Matriculas';
import Modulo, { FinderModulos, RetornoModulo } from 'Interface/Modulos';
import Plano, { FinderPlanos, RetornoPlano } from 'Interface/Planos';
import Questao, { FinderQuestoes, RetornoQuestao } from 'Interface/Questoes';
import Parametros from 'Interface/Requisicao';
import {
  FinderQuantidadePorDataTransacoes,
  FinderQuantidadeTransacoes,
  FinderValorTotalPorDataTransacoes,
  FinderValorTotalTransacoes,
} from 'Interface/Transacoes';
import Usuario, {
  FinderTotalCadastradoPorDataUsuarios,
  FinderTotalCadastradoUsuarios,
  FinderUsuarios,
  RetornoUsuario,
  Token,
} from 'Interface/Usuarios';
import { refresh } from 'Reducer/Autenticacao';
import { Estado } from 'Store';

import { formData, paramsParaString } from './utils';

const createApi = buildCreateApi(
    coreModule(),
    reactHooksModule({ unstable__sideEffectsInRender: true })
);

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: `${window.location.origin}/api/v1`,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as Estado).autenticacao.token;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const { token, refresh_token } = (api.getState() as Estado).autenticacao;

        const refreshResult = await baseQuery(
          {
            credentials: 'include',
            url: '/usuarios/refresh',
            method: 'POST',
            body: { token, refresh_token },
          },
          api,
          extraOptions
        );

        if (refreshResult.data) {
          api.dispatch(refresh(refreshResult.data));

          result = await baseQuery(args, api, extraOptions);
        } else {
          window.location.href = '/login?reset=true';
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();

      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};


const api = createApi({
    reducerPath: 'api',
    baseQuery: customBaseQuery,
    extractRehydrationInfo(action, { reducerPath }) {
      if (action.type === REHYDRATE && action.payload) {
        return action.payload[reducerPath]
      }
    },
    tagTypes: [
        'Perfil',
        'Usuarios',
        'TotalCadastradoUsuarios',
        'TotalCadastradoPorDataUsuarios',
        'Cursos',
        'TotalCadastradoCursos',
        'Categorias',
        'Modulos',
        'Aulas',
        'Matriculas',
        'AulasAssistidas',
        'AulasConcluidas',
        'Assinaturas',
        'Planos',
        'Avaliacoes',
        'Questoes',
        'Alternativas',
        'AvaliacoesRespondidas',
        'Certificados',
        'Fretes',
        'AssistirMaisTarde',
        'QuantidadeTransacoes',
        'QuantidadePorDataTransacoes',
        'ValorTotalTransacoes',
        'ValorTotalPorDataTransacoes',
        'HowManySubscriptions',
    ],
    endpoints: construtor => ({
        /**
         * Usuários
         */
        cadastrar: construtor.mutation<RetornoUsuario & Token, Partial<Usuario>>({
            query: dados => ({ url: '/usuarios/cadastrar', method: 'POST', body: dados }),
        }),
        login: construtor.mutation<RetornoUsuario & Token, Partial<Usuario>>({
            query: dados => ({ url: '/usuarios/login', method: 'POST', body: dados }),
        }),
        recuperarPerfil: construtor.query<RetornoUsuario, void>({
            query: () => '/usuarios/perfil',
            providesTags: () => [{ type: 'Perfil' }],
        }),
        editarPerfil: construtor.mutation<RetornoUsuario, Partial<Usuario> & Pick<Usuario, 'id'>>({
            query: (dados) => ({ url: '/usuarios/perfil', method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Perfil' }] : [],
        }),
        recuperarUsuarios: construtor.query<FinderUsuarios, Parametros | void>({
            query: (params) => `/usuarios?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.usuarios
                ? [
                    ...resultado.usuarios.map(({ id }) => ({ type: 'Usuarios', id } as const)),
                    { type: 'Usuarios', id: 'LISTA' },
                ]
                : [{ type: 'Usuarios', id: 'LISTA' }],
        }),
        recuperarTotalCadastradoUsuarios: construtor.query<FinderTotalCadastradoUsuarios, Parametros | void>({
            query: (params) => `/usuarios?finder=totalCadastrado&${paramsParaString(params)}`,
            providesTags: () => [{ type: 'TotalCadastradoUsuarios', id: 'LISTA' }],
        }),
        recuperarTotalCadastradoPorDataUsuarios: construtor.query<FinderTotalCadastradoPorDataUsuarios, Parametros | void>({
            query: (params) => `/usuarios?finder=totalCadastradoPorData&${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.usuarios
                ? [
                    ...resultado.usuarios.map(({ data }) => ({ type: 'TotalCadastradoPorDataUsuarios', data } as const)),
                    { type: 'TotalCadastradoPorDataUsuarios', id: 'LISTA' },
                ]
                : [{ type: 'TotalCadastradoPorDataUsuarios', id: 'LISTA' }],
        }),
        recuperarUsuario: construtor.query<RetornoUsuario, string | number>({
            query: id => `/usuarios/${id}`,
            providesTags: (resultado, erro, id) => [{ type: 'Usuarios', id }],
        }),
        adicionarUsuario: construtor.mutation<Usuario, Partial<Usuario>>({
            query: (dados) => ({ url: '/usuarios', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Usuarios', id: 'LISTA' }] : [],
        }),
        editarUsuario: construtor.mutation<Usuario, Partial<Usuario> & Pick<Usuario, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/usuarios/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Usuarios', id }] : [],
        }),
        /**
         * Categorias
         */
        recuperarCategorias: construtor.query<FinderCategorias, Parametros | void>({
            query: (params) => `/categorias?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.categorias
                ? [
                    ...resultado.categorias.map(({ id }) => ({ type: 'Categorias', id } as const)),
                    { type: 'Categorias', id: 'LISTA' },
                ]
                : [{ type: 'Categorias', id: 'LISTA' }],
        }),
        recuperarCategoria: construtor.query<RetornoCategoria, string | number>({
            query: id => `/categorias/${id}`,
            providesTags: (resultado, erro, id) => [{ type: 'Categorias', id }],
        }),
        adicionarCategoria: construtor.mutation<Categoria, Partial<Categoria>>({
            query: (dados) => ({ url: '/categorias', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Categorias', id: 'LISTA' }] : [],
        }),
        editarCategoria: construtor.mutation<Categoria, Partial<Categoria> & Pick<Categoria, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/categorias/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Categorias', id }] : [],
        }),
        /**
         * Cursos
         */
        recuperarCursos: construtor.query<FinderCursos, Parametros | void>({
            query: (params) => `/cursos?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.cursos
                ? [
                    ...resultado.cursos.map(({ id }) => ({ type: 'Cursos', id } as const)),
                    { type: 'Cursos', id: 'LISTA' },
                ]
                : [{ type: 'Cursos', id: 'LISTA' }],
        }),
        recuperarTotalCadastradoCursos: construtor.query<FinderTotalCadastradoCursos, Parametros | void>({
            query: (params) => `/cursos?finder=totalCadastrado&${paramsParaString(params)}`,
            providesTags: () => [{ type: 'TotalCadastradoCursos', id: 'LISTA' }],
        }),
        recuperarCurso: construtor.query<RetornoCurso, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/cursos/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Cursos', id }];
            },
        }),
        adicionarCurso: construtor.mutation<Curso, Partial<Curso>>({
            query: (dados) => ({ url: '/cursos', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Cursos', id: 'LISTA' }] : [],
        }),
        editarCurso: construtor.mutation<Curso, Partial<Curso> & Pick<Curso, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/cursos/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Cursos', id }] : [],
        }),
        /**
         * Módulos
         */
         recuperarModulos: construtor.query<FinderModulos, Parametros | void>({
            query: (params) => `/modulos?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.modulos
                ? [
                    ...resultado.modulos.map(({ id }) => ({ type: 'Modulos', id } as const)),
                    { type: 'Modulos', id: 'LISTA' },
                ]
                : [{ type: 'Modulos', id: 'LISTA' }],
        }),
        recuperarModulo: construtor.query<RetornoModulo, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/modulos/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Modulos', id }];
            },
        }),
        adicionarModulo: construtor.mutation<Modulo, Partial<Modulo>>({
            query: (dados) => ({ url: '/modulos', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Modulos', id: 'LISTA' }] : [],
        }),
        editarModulo: construtor.mutation<Modulo, Partial<Modulo> & Pick<Modulo, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/modulos/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Modulos', id }] : [],
        }),
        removerModulo: construtor.mutation<Modulo, Partial<Modulo> & Pick<Modulo, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/modulos/${id}`, method: 'DELETE', body: dados }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Modulos', id }] : [],
        }),
        /**
         * Aulas
         */
        recuperarAulas: construtor.query<FinderAulas, Parametros | void>({
            query: (params) => `/aulas?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.aulas
                ? [
                    ...resultado.aulas.map(({ id }) => ({ type: 'Aulas', id } as const)),
                    { type: 'Aulas', id: 'LISTA' },
                ]
                : [{ type: 'Aulas', id: 'LISTA' }],
        }),
        recuperarAula: construtor.query<RetornoAula, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/aulas/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Aulas', id }];
            },
        }),
        adicionarAula: construtor.mutation<Aula, Partial<Aula>>({
            query: (dados) => ({ url: '/aulas', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Aulas', id: 'LISTA' }, { type: 'Modulos', id: 'LISTA' }] : [],
        }),
        editarAula: construtor.mutation<Aula, Partial<Aula> & Pick<Aula, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/aulas/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Aulas', id }, { type: 'Modulos', id: resultado?.modulo_id }] : [],
        }),
        gerarEstruturaLTIAula: construtor.mutation<Aula, Partial<Aula>>({
            query: (dados) => ({ url: '/aulas/lti', method: 'POST', body: formData(dados, 'POST') }),
        }),
        recuperarUrlsIesdeAula: construtor.mutation<Aula, Partial<Aula>>({
            query: (dados) => ({ url: '/aulas/iesde', method: 'POST', body: formData(dados, 'POST') }),
        }),
        recuperarUrlsProprietarioAula: construtor.mutation<RetornoAula, Partial<Aula>>({
            query: (dados) => ({ url: '/aulas/proprietario', method: 'POST', body: formData(dados, 'POST') }),
        }),
        /**
         * Matriculas
         */
        recuperarMatriculas: construtor.query<FinderMatriculas, Parametros | void>({
            query: (params) => `/matriculas?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.matriculas
                ? [
                    ...resultado.matriculas.map(({ id }) => ({ type: 'Matriculas', id } as const)),
                    { type: 'Matriculas', id: 'LISTA' },
                ]
                : [{ type: 'Matriculas', id: 'LISTA' }],
        }),
        recuperarMatricula: construtor.query<RetornoMatricula, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/matriculas/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Matriculas', id }];
            },
        }),
        adicionarMatricula: construtor.mutation<Matricula, Partial<Matricula>>({
            query: (dados) => ({ url: '/matriculas', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Matriculas', id: 'LISTA' }] : [],
        }),
        editarMatricula: construtor.mutation<Matricula, Partial<Matricula> & Pick<Matricula, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/matriculas/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Matriculas', id }] : [],
        }),
        gerarEstruturaLTIMatricula: construtor.mutation<Matricula, Partial<Matricula>>({
            query: (dados) => ({ url: '/matriculas/lti', method: 'POST', body: formData(dados, 'POST') }),
        }),
        /**
         * Aulas Assistidas
         */
        recuperarAulasAssistidas: construtor.query<FinderAulasAssistidas, Parametros | void>({
            query: (params) => `/aulas-assistidas?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.aulas_assistidas
                ? [
                    ...resultado.aulas_assistidas.map(({ id }) => ({ type: 'AulasAssistidas', id } as const)),
                    { type: 'AulasAssistidas', id: 'LISTA' },
                ]
                : [{ type: 'AulasAssistidas', id: 'LISTA' }],
        }),
        recuperarAulaAssistida: construtor.query<RetornoAulaAssistida, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/aulas-assistidas/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'AulasAssistidas', id }];
            },
        }),
        adicionarAulaAssistida: construtor.mutation<AulaAssistida, Partial<AulaAssistida>>({
            query: (dados) => ({ url: '/aulas-assistidas', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'AulasAssistidas', id: 'LISTA' }, { type: 'Matriculas', id: 'LISTA' }, { type: 'Cursos', id: 'LISTA' }] : [],
        }),
        editarAulaAssistida: construtor.mutation<AulaAssistida, Partial<AulaAssistida> & Pick<AulaAssistida, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/aulas-assistidas/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'AulasAssistidas', id }] : [],
        }),
        /**
         * Aulas Concluidas
         */
        recuperarAulasConcluidas: construtor.query<FinderAulasConcluidas, Parametros | void>({
            query: (params) => `/aulas-concluidas?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.aulas_concluidas
                ? [
                    ...resultado.aulas_concluidas.map(({ id }) => ({ type: 'AulasConcluidas', id } as const)),
                    { type: 'AulasConcluidas', id: 'LISTA' },
                ]
                : [{ type: 'AulasConcluidas', id: 'LISTA' }],
        }),
        recuperarAulaConcluida: construtor.query<RetornoAulaConcluida, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/aulas-concluidas/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'AulasConcluidas', id }];
            },
        }),
        adicionarAulaConcluida: construtor.mutation<AulaConcluida, Partial<AulaConcluida>>({
            query: (dados) => ({ url: '/aulas-concluidas', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'AulasConcluidas', id: 'LISTA' }, { type: 'Matriculas', id: 'LISTA' }, { type: 'Cursos', id: 'LISTA' }] : [],
        }),
        editarAulaConcluida: construtor.mutation<AulaConcluida, Partial<AulaConcluida> & Pick<AulaConcluida, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/aulas-concluidas/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'AulasConcluidas', id }] : [],
        }),
        removerAulaConcluida: construtor.mutation<AulaConcluida, Partial<AulaConcluida> & Pick<AulaConcluida, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/aulas-concluidas/${id}`, method: 'POST', body: formData(dados, 'DELETE') }),
            invalidatesTags: (resultado, erro, { id, ...aula }) => !erro ? [{ type: 'AulasConcluidas', id }, { type: 'Cursos', id: aula.aula?.modulo?.curso_id }] : [],
        }),
        /**
         * Assinaturas
         */
         recuperarAssinaturas: construtor.query<FinderAssinaturas, Parametros | void>({
            query: (params) => `/assinaturas?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.assinaturas
                ? [
                    ...resultado.assinaturas.map(({ id }) => ({ type: 'Assinaturas', id } as const)),
                    { type: 'Assinaturas', id: 'LISTA' },
                ]
                : [{ type: 'Assinaturas', id: 'LISTA' }],
        }),
        recuperarAssinatura: construtor.query<RetornoAssinatura, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/assinaturas/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Assinaturas', id }];
            },
        }),
        getHowManySubscriptions: construtor.query<FinderHowManySubscriptionsInterface, Parametros | void>({
            query: (params) => `/assinaturas?finder=howMany&${paramsParaString(params)}`,
            providesTags: () => [{ type: 'HowManySubscriptions', id: 'LISTA' }],
        }),
        adicionarAssinatura: construtor.mutation<Assinatura, Partial<Assinatura>>({
            query: (dados) => ({ url: '/assinaturas', method: 'POST', body: dados }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Assinaturas', id: 'LISTA' }] : [],
        }),
        editarAssinatura: construtor.mutation<Assinatura, Partial<Assinatura> & Pick<Assinatura, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/assinaturas/${id}`, method: 'POST', body: formData(dados, 'PUT', true) }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Assinaturas', id }] : [],
        }),
        trocarCartaoDaAssinatura: construtor.mutation<Assinatura, Partial<Assinatura> & Pick<Assinatura, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/assinaturas/trocar-cartao/${id}`, method: 'PUT', body: dados }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Assinaturas', id }] : [],
        }),
        /**
         * Planos
         */
         recuperarPlanos: construtor.query<FinderPlanos, Parametros | void>({
            query: (params) => `/planos?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.planos
                ? [
                    ...resultado.planos.map(({ id }) => ({ type: 'Planos', id } as const)),
                    { type: 'Planos', id: 'LISTA' },
                ]
                : [{ type: 'Planos', id: 'LISTA' }],
        }),
        recuperarPlano: construtor.query<RetornoPlano, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/planos/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Planos', id }];
            },
        }),
        adicionarPlano: construtor.mutation<Plano, Partial<Plano>>({
            query: (dados) => ({ url: '/planos', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Planos', id: 'LISTA' }] : [],
        }),
        editarPlano: construtor.mutation<Plano, Partial<Plano> & Pick<Plano, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/planos/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Planos', id }] : [],
        }),
        /**
         * Avaliacoes
         */
         recuperarAvaliacoes: construtor.query<FinderAvaliacoes, Parametros | void>({
            query: (params) => `/avaliacoes?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.avaliacoes
                ? [
                    ...resultado.avaliacoes.map(({ id }) => ({ type: 'Avaliacoes', id } as const)),
                    { type: 'Avaliacoes', id: 'LISTA' },
                ]
                : [{ type: 'Avaliacoes', id: 'LISTA' }],
        }),
        recuperarAvaliacao: construtor.query<RetornoAvaliacao, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/avaliacoes/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Avaliacoes', id }];
            },
        }),
        adicionarAvaliacao: construtor.mutation<Avaliacao, Partial<Avaliacao>>({
            query: (dados) => ({ url: '/avaliacoes', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Avaliacoes', id: 'LISTA' }] : [],
        }),
        editarAvaliacao: construtor.mutation<Avaliacao, Partial<Avaliacao> & Pick<Avaliacao, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/avaliacoes/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Avaliacoes', id }] : [],
        }),
        removerAvaliacao: construtor.mutation<Avaliacao, Partial<Avaliacao> & Pick<Avaliacao, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/avaliacoes/${id}`, method: 'POST', body: formData(dados, 'DELETE') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Avaliacoes', id: 'Lista' }, { type: 'Avaliacoes', id }] : [],
        }),
        /**
         * Questões
         */
         recuperarQuestoes: construtor.query<FinderQuestoes, Parametros | void>({
            query: (params) => `/questoes?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.questoes
                ? [
                    ...resultado.questoes.map(({ id }) => ({ type: 'Questoes', id } as const)),
                    { type: 'Questoes', id: 'LISTA' },
                ]
                : [{ type: 'Questoes', id: 'LISTA' }],
        }),
        recuperarQuestao: construtor.query<RetornoQuestao, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/questoes/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Questoes', id }];
            },
        }),
        adicionarQuestao: construtor.mutation<Questao, Partial<Questao>>({
            query: (dados) => ({ url: '/questoes', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Questoes', id: 'LISTA' }, { type: 'Avaliacoes', id: 'LISTA' }] : [],
        }),
        editarQuestao: construtor.mutation<Questao, Partial<Questao> & Pick<Questao, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/questoes/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Questoes', id }, { type: 'Avaliacoes', id: 'LISTA' }] : [],
        }),
        removerQuestao: construtor.mutation<Questao, Partial<Questao> & Pick<Questao, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/questoes/${id}`, method: 'POST', body: formData(dados, 'DELETE') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Questoes', id }, { type: 'Avaliacoes', id: 'LISTA' }] : [],
        }),
        /**
         * Alternativas
         */
         recuperarAlternativas: construtor.query<FinderAlternativas, Parametros | void>({
            query: (params) => `/alternativas?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.alternativas
                ? [
                    ...resultado.alternativas.map(({ id }) => ({ type: 'Alternativas', id } as const)),
                    { type: 'Alternativas', id: 'LISTA' },
                ]
                : [{ type: 'Alternativas', id: 'LISTA' }],
        }),
        recuperarAlternativa: construtor.query<RetornoAlternativa, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/alternativas/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Alternativas', id }];
            },
        }),
        adicionarAlternativa: construtor.mutation<Alternativa, Partial<Alternativa>>({
            query: (dados) => ({ url: '/alternativas', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro, dados) => !erro ? [{ type: 'Alternativas', id: 'LISTA' }, { type: 'Avaliacoes', id: 'LISTA' }, { type: 'Questoes', id: dados.questao_id }] : [],
        }),
        editarAlternativa: construtor.mutation<Alternativa, Partial<Alternativa> & Pick<Alternativa, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/alternativas/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id, ...dados }) => !erro ? [{ type: 'Alternativas', id }, { type: 'Avaliacoes', id: 'LISTA' }, { type: 'Questoes', id: dados.questao_id }] : [],
        }),
        removerAlternativa: construtor.mutation<Alternativa, Partial<Alternativa> & Pick<Alternativa, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/alternativas/${id}`, method: 'POST', body: formData(dados, 'DELETE') }),
            invalidatesTags: (resultado, erro, { id, ...dados }) => !erro ? [{ type: 'Alternativas', id }, { type: 'Avaliacoes', id: 'LISTA' }, { type: 'Questoes', id: dados.questao_id }] : [],
        }),
        /**
         * Avaliações Respondidas
         */
         recuperarAvaliacoesRespondidas: construtor.query<FinderAvaliacoesRespondidas, Parametros | void>({
            query: (params) => `/avaliacoes-respondidas?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.avaliacoes_respondidas
                ? [
                    ...resultado.avaliacoes_respondidas.map(({ id }) => ({ type: 'AvaliacoesRespondidas', id } as const)),
                    { type: 'AvaliacoesRespondidas', id: 'LISTA' },
                ]
                : [{ type: 'AvaliacoesRespondidas', id: 'LISTA' }],
        }),
        recuperarAvaliacaoRespondida: construtor.query<RetornoAvaliacaoRespondida, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/avaliacoes-respondidas/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'AvaliacoesRespondidas', id }];
            },
        }),
        adicionarAvaliacaoRespondida: construtor.mutation<AvaliacaoRespondida, Partial<AvaliacaoRespondida>>({
            query: (dados) => ({ url: '/avaliacoes-respondidas', method: 'POST', body: dados }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'AvaliacoesRespondidas', id: 'LISTA' }, { type: 'Avaliacoes', id: 'LISTA' }, { type: 'Matriculas', id: 'LISTA' }] : [],
        }),
        editarAvaliacaoRespondida: construtor.mutation<AvaliacaoRespondida, Partial<AvaliacaoRespondida> & Pick<AvaliacaoRespondida, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/avaliacoes-respondidas/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'AvaliacoesRespondidas', id }] : [],
        }),
        removerAvaliacaoRespondida: construtor.mutation<AvaliacaoRespondida, Partial<AvaliacaoRespondida> & Pick<AvaliacaoRespondida, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/avaliacoes-respondidas/${id}`, method: 'POST', body: formData(dados, 'DELETE') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'AvaliacoesRespondidas', id: 'LISTA' }, { type: 'AvaliacoesRespondidas', id }] : [],
        }),
        /**
         * Certificados
         */
        recuperarCertificados: construtor.query<FinderCertificados, Parametros | void>({
            query: (params) => `/certificados?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.certificados
                ? [
                    ...resultado.certificados.map(({ id }) => ({ type: 'Certificados', id } as const)),
                    { type: 'Certificados', id: 'LISTA' },
                ]
                : [{ type: 'Certificados', id: 'LISTA' }],
        }),
        recuperarCertificado: construtor.query<RetornoCertificado, string | number | Parametros>({
            query: params => {
                let id: number;
                let query: Parametros = {};

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                    query = paramsParaString(params);
                }

                return `/certificados/${id}?${query}`;
            },
            providesTags: (resultado, erro, params) => {
                let id: number;

                if (['string', 'number'].includes(typeof params)) {
                    id = params as number;
                } else {
                    params = params as Parametros;

                    id = params.id as number;
                }

                return [{ type: 'Certificados', id }];
            },
        }),
        adicionarCertificado: construtor.mutation<Certificado, Partial<Certificado>>({
            query: (dados) => ({ url: '/certificados', method: 'POST', body: dados }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Certificados', id: 'LISTA' }, { type: 'Matriculas', id: 'LISTA' }] : [],
        }),
        editarCertificado: construtor.mutation<Certificado, Partial<Certificado> & Pick<Certificado, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/certificados/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Certificados', id }] : [],
        }),
        removerCertificado: construtor.mutation<Certificado, Partial<Certificado> & Pick<Certificado, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/certificados/${id}`, method: 'POST', body: formData(dados, 'DELETE') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Certificados', id: 'LISTA' }, { type: 'Certificados', id }] : [],
        }),
        calcularFretes: construtor.query<RetornoFretes, Parametros | void>({
            query: () => '/certificados/frete',
        }),
        rebuildCertificate: construtor.mutation<Certificado, Partial<Certificado> & Pick<Certificado, 'id'>>({
          query: ({ id }) => ({ url: `/certificados/rebuild/${id}`, method: 'POST' }),
          invalidatesTags: ['Certificados'],
        }),
        /**
         * Assistir mais tarde
         */
        recuperarAssistirMaisTarde: construtor.query<FinderAssistirMaisTarde, Parametros | void>({
            query: (params) => `/assistir-mais-tarde?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.assistir_mais_tarde
                ? [
                    ...resultado.assistir_mais_tarde.map(({ id }) => ({ type: 'AssistirMaisTarde', id } as const)),
                    { type: 'AssistirMaisTarde', id: 'LISTA' },
                ]
                : [{ type: 'AssistirMaisTarde', id: 'LISTA' }],
        }),
        adicionarAssistirMaisTarde: construtor.mutation<AssistirMaisTarde, Partial<AssistirMaisTarde>>({
            query: (dados) => ({ url: '/assistir-mais-tarde', method: 'POST', body: dados }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'AssistirMaisTarde', id: 'LISTA' }, { type: 'Cursos', id: 'LISTA' }] : [],
        }),
        removerAssistirMaisTarde: construtor.mutation<AssistirMaisTarde, Partial<AssistirMaisTarde> & Pick<AssistirMaisTarde, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/assistir-mais-tarde/${id}`, method: 'POST', body: formData(dados, 'DELETE') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'AssistirMaisTarde', id: 'LISTA' }, { type: 'AssistirMaisTarde', id }, { type: 'Cursos', id: 'LISTA' }] : [],
        }),
        /**
         * Transações
         */
        recuperarQuantidadeTransacoes: construtor.query<FinderQuantidadeTransacoes, Parametros | void>({
            query: (params) => `/transacoes?finder=quantidade&${paramsParaString(params)}`,
            providesTags: () => [{ type: 'QuantidadeTransacoes', id: 'LISTA' }],
        }),
        recuperarQuantidadePorDataTransacoes: construtor.query<FinderQuantidadePorDataTransacoes, Parametros | void>({
            query: (params) => `/transacoes?finder=quantidadePorData&${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.transacoes
                ? [
                    ...resultado.transacoes.map(({ data }) => ({ type: 'QuantidadePorDataTransacoes', data } as const)),
                    { type: 'QuantidadePorDataTransacoes', id: 'LISTA' },
                ]
                : [{ type: 'QuantidadePorDataTransacoes', id: 'LISTA' }],
        }),
        recuperarValorTotalTransacoes: construtor.query<FinderValorTotalTransacoes, Parametros | void>({
            query: (params) => `/transacoes?finder=valorTotal&${paramsParaString(params)}`,
            providesTags: () => [{ type: 'ValorTotalTransacoes', id: 'LISTA' }],
        }),
        recuperarValorTotalPorDataTransacoes: construtor.query<FinderValorTotalPorDataTransacoes, Parametros | void>({
            query: (params) => `/transacoes?finder=valorTotalPorData&${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.transacoes
                ? [
                    ...resultado.transacoes.map(({ data }) => ({ type: 'ValorTotalPorDataTransacoes', data } as const)),
                    { type: 'ValorTotalPorDataTransacoes', id: 'LISTA' },
                ]
                : [{ type: 'ValorTotalPorDataTransacoes', id: 'LISTA' }],
        }),
    }),
});

export default api;

export const {
    useRecuperarUsuariosQuery,
    useRecuperarTotalCadastradoUsuariosQuery,
    useRecuperarTotalCadastradoPorDataUsuariosQuery,
    useRecuperarUsuarioQuery,
    useAdicionarUsuarioMutation,
    useEditarUsuarioMutation,
    useCadastrarMutation,
    useLoginMutation,
    useRecuperarPerfilQuery,
    useEditarPerfilMutation,
    useRecuperarCategoriasQuery,
    useRecuperarCategoriaQuery,
    useAdicionarCategoriaMutation,
    useEditarCategoriaMutation,
    useRecuperarCursosQuery,
    useRecuperarTotalCadastradoCursosQuery,
    useRecuperarCursoQuery,
    useAdicionarCursoMutation,
    useEditarCursoMutation,
    useRecuperarModulosQuery,
    useRecuperarModuloQuery,
    useAdicionarModuloMutation,
    useEditarModuloMutation,
    useRemoverModuloMutation,
    useRecuperarAulasQuery,
    useRecuperarAulaQuery,
    useAdicionarAulaMutation,
    useEditarAulaMutation,
    useGerarEstruturaLTIAulaMutation,
    useRecuperarUrlsIesdeAulaMutation,
    useRecuperarUrlsProprietarioAulaMutation,
    useRecuperarMatriculasQuery,
    useRecuperarMatriculaQuery,
    useAdicionarMatriculaMutation,
    useEditarMatriculaMutation,
    useRecuperarAulasAssistidasQuery,
    useRecuperarAulaAssistidaQuery,
    useAdicionarAulaAssistidaMutation,
    useEditarAulaAssistidaMutation,
    useRecuperarAulasConcluidasQuery,
    useRecuperarAulaConcluidaQuery,
    useAdicionarAulaConcluidaMutation,
    useEditarAulaConcluidaMutation,
    useRemoverAulaConcluidaMutation,
    useRecuperarAssinaturasQuery,
    useRecuperarAssinaturaQuery,
    useGetHowManySubscriptionsQuery,
    useAdicionarAssinaturaMutation,
    useEditarAssinaturaMutation,
    useTrocarCartaoDaAssinaturaMutation,
    useRecuperarPlanosQuery,
    useRecuperarPlanoQuery,
    useAdicionarPlanoMutation,
    useEditarPlanoMutation,
    useRecuperarAvaliacoesQuery,
    useRecuperarAvaliacaoQuery,
    useAdicionarAvaliacaoMutation,
    useEditarAvaliacaoMutation,
    useRemoverAvaliacaoMutation,
    useRecuperarQuestoesQuery,
    useRecuperarQuestaoQuery,
    useAdicionarQuestaoMutation,
    useEditarQuestaoMutation,
    useRemoverQuestaoMutation,
    useRecuperarAlternativasQuery,
    useRecuperarAlternativaQuery,
    useAdicionarAlternativaMutation,
    useEditarAlternativaMutation,
    useRemoverAlternativaMutation,
    useRecuperarAvaliacoesRespondidasQuery,
    useRecuperarAvaliacaoRespondidaQuery,
    useAdicionarAvaliacaoRespondidaMutation,
    useEditarAvaliacaoRespondidaMutation,
    useRemoverAvaliacaoRespondidaMutation,
    useRecuperarCertificadosQuery,
    useRecuperarCertificadoQuery,
    useAdicionarCertificadoMutation,
    useEditarCertificadoMutation,
    useRemoverCertificadoMutation,
    useCalcularFretesQuery,
    useRebuildCertificateMutation,
    useRecuperarAssistirMaisTardeQuery,
    useAdicionarAssistirMaisTardeMutation,
    useRemoverAssistirMaisTardeMutation,
    useRecuperarQuantidadeTransacoesQuery,
    useRecuperarQuantidadePorDataTransacoesQuery,
    useRecuperarValorTotalTransacoesQuery,
    useRecuperarValorTotalPorDataTransacoesQuery,
} = api;
